import { FileText } from "lucide-react";

// ============================================================================
// GESTIMMONY — Page États des lieux (/app/etats-des-lieux)
// ============================================================================
// Gestion des états des lieux d'entrée et de sortie :
//   - Liste des EDL avec nombre de pièces et de photos
//   - Création via modale : pièces personnalisables, état (excellent/bon/
//     moyen/mauvais), observations, photos par pièce (base64, max 3 Mo)
//   - Comparaison automatique entrée vs sortie (même pièce, état différent
//     → surlignage amber + avertissement)
//   - Signature électronique des 2 parties avec horodatage
//
// API consommées :
//   GET/POST/PUT/DELETE /api/inspections[...]   (CRUD)
//   GET  /api/inspections/compare/{leaseId}     (comparaison entrée/sortie)
//   POST /api/inspections/{id}/sign             (signature)
// ============================================================================
import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, ClipboardCheck, ImageIcon, ArrowRightLeft, X, PenLine, Check } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/app/SignaturePad";

// Échelle d'état d'une pièce + couleur de badge associée (vert → rouge)
const CONDITIONS = [
  { value: "excellent", label: "Excellent", color: "bg-emerald-100 text-emerald-800" },
  { value: "bon", label: "Bon", color: "bg-blue-50 text-blue-800" },
  { value: "moyen", label: "Moyen", color: "bg-amber-100 text-amber-800" },
  { value: "mauvais", label: "Mauvais", color: "bg-rose-100 text-rose-800" },
];

// Convertit un fichier image (input type="file") en data URL base64.
// C'est ce format qui est stocké en base et affiché dans les <img>.
async function fileToDataUrl(file) {
  const reader = new FileReader();
  return new Promise((res, rej) => {
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}

// Pièces proposées par défaut à la création d'un EDL (modifiables)
const DEFAULT_ROOMS = ["Entrée", "Séjour", "Cuisine", "Chambre 1", "Salle de bain", "WC"];

// Équipements obligatoires d'un logement meublé (décret n°2015-981, loi ALUR)
const ALUR_ITEMS = [
  "Literie avec couette ou couverture",
  "Dispositif d'occultation des fenêtres (chambres)",
  "Plaques de cuisson",
  "Four ou four à micro-ondes",
  "Réfrigérateur et congélateur (ou compartiment ≤ -6°C)",
  "Vaisselle nécessaire aux repas",
  "Ustensiles de cuisine",
  "Table et sièges",
  "Étagères de rangement",
  "Luminaires",
  "Matériel d'entretien ménager adapté au logement",
];

export default function InspectionsPage() {
  const { user } = useAuth();
  const { canWrite } = planContext(user);
  // --- États de la page ---
  const [items, setItems] = useState([]);            // liste des EDL
  const [leases, setLeases] = useState([]);          // baux (menu déroulant)
  const [open, setOpen] = useState(false);           // modale de création
  const [compareLease, setCompareLease] = useState(null);  // bail en cours de comparaison
  const [comparison, setComparison] = useState(null);      // données entrée/sortie
  const [form, setForm] = useState(null);            // formulaire (null = fermé)

  // Charge EDL + baux en parallèle au montage
  const load = async () => {
    const [i, l] = await Promise.all([api.get("/inspections"), api.get("/leases")]);
    setItems(i.data); setLeases(l.data);
  };
  useEffect(() => { load(); }, []);

  // Ouvre la modale avec le gabarit par défaut : type "entrée", date du jour,
  // les 6 pièces standards pré-remplies en état "bon"
  const openNew = () => {
    if (!canWrite) return;
    setForm({
      lease_id: "",
      type: "entree",
      date: new Date().toISOString().slice(0, 10),
      rooms: DEFAULT_ROOMS.map((n) => ({ name: n, condition: "bon", notes: "", photos: [] })),
      general_notes: "",
      signed_by_landlord: false,
      signed_by_tenant: false,
      furnished: false,
      inventory: [],
    });
    setOpen(true);
  };

  // --- Manipulation des pièces du formulaire ---
  // Chaque update recrée le tableau rooms (immutabilité React : on ne mute
  // jamais l'état directement, sinon pas de re-render)
  const addRoom = () => {
    setForm({ ...form, rooms: [...form.rooms, { name: "Pièce " + (form.rooms.length + 1), condition: "bon", notes: "", photos: [] }] });
  };
  const removeRoom = (idx) => {
    setForm({ ...form, rooms: form.rooms.filter((_, i) => i !== idx) });
  };
  const updateRoom = (idx, patch) => {
    const rooms = [...form.rooms];
    rooms[idx] = { ...rooms[idx], ...patch };   // fusion partielle du champ modifié
    setForm({ ...form, rooms });
  };
  // Ajout de photos : fichiers > 3 Mo refusés (le base64 gonfle ~33 % et
  // encombre MongoDB). Conversion séquentielle en data URL.
  const addPhotos = async (idx, files) => {
    const dataUrls = [];
    for (const f of files) {
      if (f.size > 3 * 1024 * 1024) { toast.error(`${f.name} trop lourd (>3 Mo)`); continue; }
      dataUrls.push(await fileToDataUrl(f));
    }
    const rooms = [...form.rooms];
    rooms[idx].photos = [...(rooms[idx].photos || []), ...dataUrls];
    setForm({ ...form, rooms });
  };
  const removePhoto = (idx, pi) => {
    const rooms = [...form.rooms];
    rooms[idx].photos = rooms[idx].photos.filter((_, i) => i !== pi);
    setForm({ ...form, rooms });
  };

  // --- Inventaire mobilier (logement meublé) ---
  const toggleFurnished = (checked) => {
    if (checked && (!form.inventory || form.inventory.length === 0)) {
      setForm({ ...form, furnished: true,
        inventory: ALUR_ITEMS.map((it) => ({ item: it, present: true, quantity: 1, condition: "bon", notes: "" })) });
    } else {
      setForm({ ...form, furnished: checked });
    }
  };
  const updateInv = (idx, patch) => {
    const inventory = [...form.inventory];
    inventory[idx] = { ...inventory[idx], ...patch };
    setForm({ ...form, inventory });
  };
  const addInv = () => setForm({ ...form, inventory: [...(form.inventory || []), { item: "", present: true, quantity: 1, condition: "bon", notes: "" }] });
  const removeInv = (idx) => setForm({ ...form, inventory: form.inventory.filter((_, i) => i !== idx) });

  // Enregistre l'état des lieux complet (pièces + photos + notes)
  const save = async () => {
    if (!canWrite) return;
    try {
      await api.post("/inspections", form);
      toast.success("État des lieux enregistré");
      setOpen(false); setForm(null); load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  // Suppression avec confirmation native
  const remove = async (id) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer cet état des lieux ?")) return;
    await api.delete(`/inspections/${id}`); load();
  };

  // Ouvre la modale de comparaison : charge côte-à-côte l'EDL d'entrée
  // et de sortie du même bail (le plus récent de chaque type côté backend)
  const openCompare = async (leaseId) => {
    setCompareLease(leaseId);
    const { data } = await api.get(`/inspections/compare/${leaseId}`);
    setComparison(data);
  };

  // --- Signature électronique (même logique que la page Baux) ---
  const [signState, setSignState] = useState({ open: false, id: null, role: "landlord", initial: null });
  const openSign = (insp, role) => setSignState({
    open: true, id: insp.id, role,
    initial: role === "landlord" ? insp.signature_landlord : insp.signature_tenant,
  });
  const saveSignature = async (dataUrl) => {
    try {
      await api.post(`/inspections/${signState.id}/sign`, { role: signState.role, signature: dataUrl });
      toast.success("Signature enregistrée");
      load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/inspections/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener"; a.download = "etat-des-lieux.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("PDF indisponible"); }
  };

  // Badge coloré du type d'EDL : bleu pour entrée, violet pour sortie
  const typeBadge = (t) => (
    <Badge className={`border-0 ${t === "entree" ? "bg-blue-50 text-blue-800" : "bg-purple-50 text-purple-800"}`}>
      {t === "entree" ? "Entrée" : "Sortie"}
    </Badge>
  );

  return (
    <div className="space-y-6" data-testid="inspections-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">États des lieux</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} état(s) enregistré(s).</p>
        </div>
        <Button data-testid="inspection-new-btn" onClick={openNew} disabled={!canWrite} className="bg-[hsl(var(--primary))]">
          <Plus className="h-4 w-4 mr-2" /> Nouvel état des lieux
        </Button>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardCheck className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">Aucun état des lieux.</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Bien</TableHead>
                <TableHead>Locataire</TableHead>
                <TableHead>Pièces</TableHead>
                <TableHead>Photos</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => {
                // Le backend renvoie photo_count au lieu des photos (allègement liste)
                const photoCount = (i.rooms || []).reduce((s, r) => s + (r.photo_count || 0), 0);
                return (
                  <TableRow key={i.id} data-testid={`inspection-row-${i.id}`}>
                    <TableCell className="text-sm">{fmtDate(i.date)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {typeBadge(i.type)}
                        {i.furnished && <Badge className="border-0 bg-amber-100 text-amber-800" data-testid={`inspection-furnished-badge-${i.id}`}>Meublé</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{i.property_label}</TableCell>
                    <TableCell className="text-sm">{i.tenant_name}</TableCell>
                    <TableCell className="text-sm">{(i.rooms || []).length}</TableCell>
                    <TableCell className="text-sm flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{photoCount}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openCompare(i.lease_id)} data-testid={`inspection-compare-${i.id}`}>
                        <ArrowRightLeft className="h-4 w-4 mr-1" /> Comparer
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openSign(i, "landlord")} data-testid={`inspection-sign-landlord-${i.id}`}
                        className={i.signature_landlord ? "text-emerald-700" : ""}
                        title={i.signature_landlord_at ? `Bailleur — signé le ${i.signature_landlord_at.slice(0,10)}` : "Signature du bailleur"}>
                        {i.signature_landlord ? <Check className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
                        {i.signature_landlord_at && <span className="ml-1 text-[10px] text-emerald-600/80">{i.signature_landlord_at.slice(5,10)}</span>}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openSign(i, "tenant")} data-testid={`inspection-sign-tenant-${i.id}`}
                        className={i.signature_tenant ? "text-emerald-700" : ""}
                        title={i.signature_tenant_at ? `Locataire — signé le ${i.signature_tenant_at.slice(0,10)}` : "Signature du locataire"}>
                        {i.signature_tenant ? <Check className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
                        {i.signature_tenant_at && <span className="ml-1 text-[10px] text-emerald-600/80">{i.signature_tenant_at.slice(5,10)}</span>}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => downloadPdf(i.id)} data-testid={`inspection-pdf-${i.id}`} title="Télécharger le PDF"><FileText className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => remove(i.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvel état des lieux</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Bail</Label>
                  <Select value={form.lease_id} onValueChange={(v) => setForm({ ...form, lease_id: v })}>
                    <SelectTrigger data-testid="inspection-lease"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.property_label} — {l.tenant_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="inspection-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entree">Entrée</SelectItem>
                      <SelectItem value="sortie">Sortie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="inspection-furnished-toggle">
                  <input type="checkbox" checked={!!form.furnished} onChange={(e) => toggleFurnished(e.target.checked)} className="h-4 w-4 accent-teal-600" />
                  <span className="text-sm font-medium">Logement meublé — inventaire du mobilier (loi ALUR)</span>
                </label>
                {form.furnished && (
                  <div className="mt-3 space-y-2" data-testid="inspection-inventory">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500">Équipements obligatoires d'un meublé. Décochez ce qui est absent, ajustez la quantité et l'état.</p>
                      <Button variant="outline" size="sm" onClick={addInv}><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter</Button>
                    </div>
                    <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                      <span className="col-span-1">Présent</span>
                      <span className="col-span-5">Équipement</span>
                      <span className="col-span-1">Qté</span>
                      <span className="col-span-2">État</span>
                      <span className="col-span-2">Remarque</span>
                      <span className="col-span-1"></span>
                    </div>
                    {(form.inventory || []).map((it, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-slate-50/60 rounded px-2 py-1.5">
                        <input type="checkbox" checked={!!it.present} onChange={(e) => updateInv(idx, { present: e.target.checked })} className="h-4 w-4 accent-teal-600 md:col-span-1" data-testid={`inv-present-${idx}`} title="Présent" />
                        <Input className="md:col-span-5" value={it.item} onChange={(e) => updateInv(idx, { item: e.target.value })} placeholder="Équipement" />
                        <Input className="md:col-span-1" type="number" min="0" value={it.quantity} onChange={(e) => updateInv(idx, { quantity: Number(e.target.value) })} title="Quantité" />
                        <Select value={it.condition} onValueChange={(v) => updateInv(idx, { condition: v })}>
                          <SelectTrigger className="md:col-span-2"><SelectValue /></SelectTrigger>
                          <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="md:col-span-2" value={it.notes} onChange={(e) => updateInv(idx, { notes: e.target.value })} placeholder="Remarque" />
                        <Button variant="ghost" size="icon" className="md:col-span-1" onClick={() => removeInv(idx)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Pièces ({form.rooms.length})</Label>
                  <Button variant="outline" size="sm" onClick={addRoom}><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter</Button>
                </div>
                <div className="mt-2 space-y-3">
                  {form.rooms.map((r, idx) => (
                    <Card key={idx} className="p-3 border-slate-200 shadow-none">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-start">
                        <Input className="md:col-span-2" value={r.name} onChange={(e) => updateRoom(idx, { name: e.target.value })} placeholder="Nom pièce" />
                        <Select value={r.condition} onValueChange={(v) => updateRoom(idx, { condition: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input className="md:col-span-2" value={r.notes} onChange={(e) => updateRoom(idx, { notes: e.target.value })} placeholder="Observations" />
                        <Button variant="ghost" size="icon" onClick={() => removeRoom(idx)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </div>
                      <div className="mt-2 flex items-center flex-wrap gap-2">
                        {(r.photos || []).map((p, pi) => (
                          <div key={pi} className="relative group">
                            <img src={p} alt="" className="h-16 w-16 object-cover rounded-md border border-slate-200" />
                            <button
                              type="button"
                              onClick={() => removePhoto(idx, pi)}
                              className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <label className="h-16 w-16 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-slate-50">
                          <ImageIcon className="h-4 w-4 text-slate-400" />
                          <input type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(idx, Array.from(e.target.files))} />
                        </label>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <Label>Observations générales</Label>
                <Textarea rows={2} value={form.general_notes} onChange={(e) => setForm({ ...form, general_notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="inspection-save" onClick={save} className="bg-[hsl(var(--primary))]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparison Dialog */}
      <Dialog open={!!compareLease} onOpenChange={(v) => { if (!v) { setCompareLease(null); setComparison(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Comparaison Entrée / Sortie</DialogTitle></DialogHeader>
          {comparison && (
            // Deux colonnes : entrée à gauche, sortie à droite.
            // Une pièce dont l'état a changé entre les deux est surlignée amber.
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {["entree", "sortie"].map((k) => {
                const insp = comparison[k];
                return (
                  <div key={k}>
                    <div className="flex items-center gap-2 mb-3">
                      {typeBadge(k)}
                      {insp && <span className="text-xs text-slate-500">{fmtDate(insp.date)}</span>}
                    </div>
                    {!insp ? (
                      <div className="text-sm text-slate-400 py-6">Aucun état {k === "entree" ? "d'entrée" : "de sortie"} enregistré.</div>
                    ) : (
                      <div className="space-y-3">
                        {(insp.rooms || []).map((r, i) => {
                          // On cherche la pièce du même nom dans l'autre EDL
                          // pour détecter une dégradation / amélioration
                          const other = comparison[k === "entree" ? "sortie" : "entree"];
                          const otherRoom = other?.rooms?.find((x) => x.name === r.name);
                          const changed = otherRoom && otherRoom.condition !== r.condition;
                          const cCol = CONDITIONS.find((c) => c.value === r.condition);
                          return (
                            <Card key={i} className={`p-3 border shadow-none ${changed ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
                              <div className="flex items-center justify-between">
                                <div className="font-medium text-sm">{r.name}</div>
                                <Badge className={`${cCol?.color} border-0`}>{cCol?.label}</Badge>
                              </div>
                              {r.notes && <div className="text-xs text-slate-500 mt-1">{r.notes}</div>}
                              {r.photos && r.photos.length > 0 && (
                                <div className="mt-2 flex gap-1 flex-wrap">
                                  {r.photos.map((p, pi) => <img key={pi} src={p} className="h-14 w-14 object-cover rounded border border-slate-200" alt="" />)}
                                </div>
                              )}
                              {changed && (
                                <div className="mt-2 text-xs text-amber-800 bg-amber-100 px-2 py-1 rounded">
                                  ⚠ Différence détectée par rapport à l'état {k === "entree" ? "de sortie" : "d'entrée"}
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    )}
                    {insp && insp.furnished && (insp.inventory || []).length > 0 && (
                      <div className="mt-4" data-testid={`compare-inventory-${k}`}>
                        <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">Inventaire mobilier (meublé)</div>
                        <div className="space-y-1">
                          {insp.inventory.map((it, ii) => {
                            const cCol = CONDITIONS.find((c) => c.value === it.condition);
                            return (
                              <div key={ii} className={`flex items-center justify-between gap-2 text-xs rounded px-2 py-1 ${it.present ? "bg-slate-50" : "bg-rose-50 line-through text-rose-700"}`}>
                                <span className="truncate">{it.item}{it.quantity > 1 ? ` ×${it.quantity}` : ""}</span>
                                {it.present && cCol && <Badge className={`${cCol.color} border-0 shrink-0`}>{cCol.label}</Badge>}
                                {!it.present && <span className="shrink-0 font-medium">Absent</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SignaturePad
        open={signState.open}
        onOpenChange={(v) => setSignState({ ...signState, open: v })}
        onSave={saveSignature}
        title={signState.role === "landlord" ? "Signature du bailleur" : "Signature du locataire"}
        initialSignature={signState.initial}
      />
    </div>
  );
}
